<template>
  <section class="sidebar-card">
    <button type="button" class="card-header card-toggle" :class="{ 'is-static': forceOpen }"
      :aria-expanded="forceOpen || open" @click="forceOpen ? null : (open = !open)">
      <div class="card-heading">
        <div class="card-eyebrow">Help</div>
        <h3 class="card-title">Controls</h3>
      </div>
      <v-icon v-if="!forceOpen" size="20" color="#8aa0aa">{{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </button>

    <div v-show="forceOpen || open" class="card-body">
      <div v-for="(instruction, index) in instructions" :key="index" class="control-row">
        <div class="control-icon">
          <v-icon size="18" color="#66c7b4">{{ instruction.icon }}</v-icon>
        </div>
        <span class="control-text">{{ instruction.text }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import { useDeviceCapability } from '@/composable/useDeviceCapability';

export default {
  name: "ModelNavigationToolsCard",
  props: {
    // When hosted in the viewer tool rail the card is always expanded (the rail icon is the toggle).
    forceOpen: { type: Boolean, default: false },
  },
  setup() {
    const { isDesktopPointer } = useDeviceCapability();
    return { isDesktopPointer };
  },
  data() {
    return {
      // Reference help — handy once, then out of the way. Collapsed by default.
      open: false,
    }
  },
  computed: {
    // Show touch gestures on touch devices (incl. large landscape tablets), mouse controls otherwise.
    instructions() {
      if (this.isDesktopPointer) {
        return [
          { icon: "mdi-mouse-left-click-outline", text: "Hold left click to rotate the model." },
          { icon: "mdi-cursor-default-click-outline", text: "Double click to select a point." },
          { icon: "mdi-mouse-scroll-wheel", text: "Scroll wheel to zoom in/out." },
          { icon: "mdi-mouse-move-vertical", text: "Hold scroll wheel to pan." },
        ];
      }
      return [
        { icon: "mdi-rotate-3d-variant", text: "Drag with one finger to rotate the model." },
        { icon: "mdi-gesture-double-tap", text: "Double tap to select a point." },
        { icon: "mdi-gesture-pinch", text: "Pinch to zoom in/out." },
        { icon: "mdi-gesture-swipe", text: "Drag with two fingers to pan." },
      ];
    },
  },
}
</script>

<style scoped>
.sidebar-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  padding: 12px 14px 8px;
}

/* Collapsible header acts as the accordion toggle */
.card-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  padding-bottom: 12px;
}

.card-toggle:hover .card-title {
  color: var(--duallo-accent);
}

.card-toggle.is-static { cursor: default; }
.card-toggle.is-static:hover .card-title { color: #ffffff; }

.card-heading {
  min-width: 0;
}

.card-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 2px 0 0;
  color: #ffffff;
}

.card-body {
  padding: 4px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.control-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-icon {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(102, 199, 180, 0.08);
  border: 1px solid rgba(102, 199, 180, 0.15);
}

.control-text {
  font-size: 0.82rem;
  color: #cfd8dc;
  line-height: 1.4;
}
</style>
